import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import Api from '@app/api'
import Loader from '@app/components/ui/placeholders/Loader'
import Placeholder from '@app/components/ui/placeholders/Placeholder'
import _ from 'lodash'
import classnames from 'classnames'
import { heading } from '@app/components/shared/StateLabel/Label'
import { Score } from '@app/constants'
import { Can } from '@app/context/ability-context'

import SVG from 'react-inlinesvg'
import downloadIcon from '@icons/download/primary'
import downloadIconBlack from '@icons/download/default'

import Participation from '@app/models/participation'

const markerClasses = (score) => {
  return classnames(
    'stats-table__marker',
    { 'stats-table__marker--right': score === Score.max },
    { 'stats-table__marker--wrong': score === Score.min },
    { 'stats-table__marker--none': score === null }
  )
}

const idForTaskCell = (task_id, keyPrefix) => `${keyPrefix}-task-${task_id}`;

const toggleHoverOn = (task_id, keyPrefix, force) => {
  let taskName = document.getElementById(idForTaskCell(task_id, keyPrefix))
  if (!taskName) { return }

  taskName.classList.toggle("stats-table__th-card--active", force)
}

const handleMouseEnter = (task_id, keyPrefix) => toggleHoverOn(task_id, keyPrefix, true)
const handleMouseLeave = (task_id, keyPrefix) => toggleHoverOn(task_id, keyPrefix, false)

const Scoring = ({ olympiad }) => {
  return (
    <Fragment>
      <div className="f--default f--l mt-gutter-s">
        { I18n.t('awards.diplom') }
        &#58;&nbsp;
        {
          I18n.t('shared.olymp_text.n_and_more', {
            amount: olympiad.diploma_score
          })
        }
      </div>
      <div className="f--default f--l">
        { I18n.t('awards.gramota') }
        &#58;&nbsp;
        {
          I18n.t('shared.olymp_text.points_from_to', {
            from: olympiad.charter_score,
            to: olympiad.diploma_score - 1
          })
        }
      </div>
      <div className="f--default f--l">
        { I18n.t('awards.cert') }
        &#58;&nbsp;
        {
          I18n.t('shared.olymp_text.n_and_less', {
            amount: olympiad.charter_score - 1
          })
        }
      </div>
    </Fragment>
  )
}

const TotalHeader = ({ keyPrefix }) => {
  return (
    <th
      id={idForTaskCell(0, keyPrefix)}
      className="stats-table__th stats-table__th-overall stats-table__th-card"
    >
      <span className="stats-table__th-inner">
        { I18n.t('olympic_participations.stats.total') }
      </span>
    </th>
  )
}

const TotalCell = ({ tasks, keyPrefix }) => {
  return (
    <td
      className="stats-table__td stats-table__card stats-table__overal"
      onMouseEnter={ () => handleMouseEnter(0, keyPrefix) }
      onMouseLeave={ () => handleMouseLeave(0, keyPrefix) }
    >
      { _.sumBy(tasks, 'answer.score') || 0 }
    </td>
  )
}

const TableHeader = ({ mode, tour, tasks, keyPrefix }) => {
  return (
    <thead>
      <tr>
        { mode === "teacher" ? (
            <th className="stats-table__th stats-table__th-number" />
          ) : null }

        { mode === "teacher" ? (
            <th className="stats-table__th stats-table__th-name" />
          ) : null }

        { tour === "main" ? <TotalHeader keyPrefix={keyPrefix} /> : null }

        {
          _.map(tasks, (task) => {
            return (
              <th
                key={task.id}
                id={idForTaskCell(task.id, keyPrefix)}
                className="stats-table__th stats-table__th-card"
              >
                <span className="stats-table__th-inner">
                  { task.name }
                </span>
              </th>
            )
          })
        }
      </tr>
    </thead>
  )
}

const Certificate = ({ diplomas, event, student }) => {
  if (!diplomas || !diplomas[event.id]) { return null }

  const object = diplomas[event.id]
  if (object.fetching) { return null }

  const reward = _.find(object.object, ['student.id', student.id])
  if (!reward || !reward.student || !reward.student.isParticipated) { return null }

  return (
    <Can I="read" of="Diplomas">
      <br/>
      <a href={reward.student.participation.download}
         target="_blank"
         className="link link--icon-right f--upper">

         { I18n.t('awards.download') }
         <SVG src={downloadIcon} />
      </a>
    </Can>
  )
}

const DownloadAll = ({ diplomas, event, group }) => {
  if (!diplomas || !diplomas[event.id]) { return null }

  const object = diplomas[event.id]

  if (object.fetching) { return null }
  if (!_.some(object.object, (o) => o.student.isParticipated)) { return null }

  return (
    <Can I="read" of="Diplomas">
      <div style={{ display: 'grid', maxWidth: '328px' }}>
        <a href={Api.teachers.diplomas({class_id: group.object.id}).download.path}
           target="_blank"
           className="btn btn--secondary btn--upper btn--icon-right mt-gutter-s mb-gutter-m"
        >
          {I18n.t('awards.download_all')}
          <SVG src={downloadIconBlack}/>
        </a>
      </div>
    </Can>
  )
}

const TableBody = ({mode, objects, tour, diplomas, keyPrefix, event}) => {
  const sortedObjects = [...objects].sort((a, b) => {
    const totalA = _.sumBy(a.tasks, 'answer.score') || 0;
    const totalB = _.sumBy(b.tasks, 'answer.score') || 0;
    return totalB - totalA;
  });

  return (
    <tbody>
      {
        _.map(sortedObjects, (result, index) => {
          const student = result.student

          return (
            <tr key={student.id} className="stats-table__tr">
              { mode === "teacher" ? (
                  <td className="stats-table__td stats-table__number">
                    { `${index + 1}.` }
                  </td>
                ) : null }

              { mode === "teacher" ? (
                  <td className="stats-table__td stats-table__student">
                    { student.fullName }
                  </td>
                ) : null }

              { tour === "main" ? (
                  <TotalCell tasks={result.tasks} keyPrefix={keyPrefix} />
                ) : null }

              {
                _.map(result.tasks, ({ task, answer }) => {
                  const score = answer ? answer.score : null

                  return (
                    <td
                      key={`${student.id}/${task.id}`}
                      className="stats-table__td stats-table__card"
                      onMouseEnter={ () => handleMouseEnter(task.id, keyPrefix) }
                      onMouseLeave={ () => handleMouseLeave(task.id, keyPrefix) }
                    >
                      {
                        tour === "main" ? (
                          answer ? (score || 0) : String.fromCharCode(8212)
                        ) : (
                          <span className={markerClasses(score)} />
                        )
                      }
                    </td>
                  )
                })
              }
            </tr>
          )
        })
      }
    </tbody>
  )
}

const Legend = ({ tour }) => {
  if (tour === "main") { return <div className="separator mb-0" />; }

  return (
    <Fragment>
      <div className="separator mb-0" />
      <div className="legend">
        <div className="legend__mark legend__mark--right">
          { I18n.t('main_results.task.solved_correctly') }
        </div>
        <div className="legend__mark legend__mark--wrong">
          { I18n.t('main_results.task.solved_incorrectly') }
        </div>
        <div className="legend__mark legend__mark--none">
          { I18n.t('main_results.task.not_started') }
        </div>
      </div>
      <div className="separator mt-0" />
    </Fragment>
  )
}

const StatisticsTable = (props) => {
  const { olympiad, event, group, tour, tasks } = props
  const statistics = group.statistics && group.statistics[tour]

  if (!statistics || statistics.fetching) { return <Loader /> }
  if (!statistics.object.length) { return null }

  const diplomas = group.diplomas

  return (
    <Fragment>
      <div className="stats-table">
        <h3 className="mt-gutter-s mb-gutter-s f--regular">
          { heading(tour) }
        </h3>

        <Can I="read" a="scoring">
          { tour === "main" ? (
              <Fragment>
                <div className="f--default f--bold f--l">
                  { I18n.t('awards.scoring') }
                </div>
                <Scoring olympiad={olympiad} />
              </Fragment>
            ) : null
          }
        </Can>

        <Legend tour={tour} />

        <div className="stats-table__table-wrapper">
          <table className="stats-table__table">
            <TableHeader
              mode="teacher"
              tour={tour}
              tasks={tasks}
            />

            <TableBody
              mode="teacher"
              tour={tour}
              objects={_.values(statistics.object)}
              diplomas={diplomas}
              event={event}
            />
          </table>
        </div>
      </div>
    </Fragment>
  )
}

StatisticsTable.propTypes = {
  tasks: PropTypes.array.isRequired,
  olympiad: PropTypes.object.isRequired,
  tour: PropTypes.oneOf(['preparatory', 'trial', 'main']).isRequired,
  group: PropTypes.object.isRequired,
}

export default StatisticsTable

const StudentScoring = ({ student }) => {
  const olympiad = student.participation?.olympiad
  if (!olympiad) { return null }

  return (<Scoring olympiad={olympiad} />)
}

export const ChildStatisticsTable = ({ student_id, tours }) => {
  const keyPrefix = `table-${student_id}`

  return _.map(tours, ({tour, student, tasks}) => {
    const cards = _.map(tasks, 'task')

    return (
      <div className="stats-table" key={`${keyPrefix}-{tour}`}>
        <h3 className="mt-gutter-s mb-gutter-s f--regular">
          { heading(tour) }
        </h3>
        { tour === "main" ? (
            <Can I="read" a="scoring">
              <Fragment>
                <div className="f--default f--bold f--l">
                  { I18n.t('awards.scoring') }
                </div>
                <StudentScoring student={student} />
                <div className="separator" />
              </Fragment>
            </Can>
          ) : null
        }
        <Legend tour={tour} />
        <div className="stats-table__table-wrapper">
          <table className="stats-table__table">
            <TableHeader
              mode="parent"
              tour={tour}
              tasks={cards}
              keyPrefix={keyPrefix}
            />

            <TableBody
              mode="parent"
              tour={tour}
              objects={[{student, tasks}]}
              keyPrefix={keyPrefix}
            />
          </table>
        </div>
      </div>
    )
  })
}
